import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  BehaviorDrainCoordinator,
  behaviorDrainCoordinator,
} from "./behaviorDrainCoordinator.js";

const BehaviorDrainCoordinatorContext = createContext<BehaviorDrainCoordinator>(
  behaviorDrainCoordinator,
);

/**
 * Returns the behavior drain coordinator for the current layout render tree.
 * Defaults to the app-wide singleton (displays, control room).
 */
export function useBehaviorDrainCoordinator(): BehaviorDrainCoordinator {
  return useContext(BehaviorDrainCoordinatorContext);
}

type BehaviorDrainCoordinatorProviderProps = {
  children: ReactNode;
  /** When true, uses a dedicated coordinator for this subtree (list previews). */
  isolate?: boolean;
  /** Recreate the isolated coordinator when the layout identity changes. */
  layoutId?: string;
};

/**
 * Optionally scopes behavior drains to a private coordinator instance so
 * multiple {@link LayoutRenderer} previews on one page do not overwrite
 * each other's scene catalog runtimes.
 */
export function BehaviorDrainCoordinatorProvider({
  children,
  isolate = false,
  layoutId,
}: BehaviorDrainCoordinatorProviderProps) {
  const isolatedCoordinator = useMemo(
    () => (isolate ? new BehaviorDrainCoordinator() : null),
    [isolate, layoutId],
  );

  if (!isolatedCoordinator) {
    return <>{children}</>;
  }

  return (
    <BehaviorDrainCoordinatorContext.Provider value={isolatedCoordinator}>
      {children}
    </BehaviorDrainCoordinatorContext.Provider>
  );
}
